import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  KeyRound, Plus, Pencil, Trash2, Power, PowerOff, PlugZap, Loader, AlertTriangle, Info,
  CheckCircle2, XCircle, Clock, ArrowDownToLine, X,
} from 'lucide-react';
import { aiAdmin } from '../services/api';
import { Dialog, DialogHeader, DialogBody, DialogFooter, FormSection, Field } from './Dialog';

/**
 * AI providers in Settings (admins): the keys the AI features use (Lab
 * insights for now), their deployments and prices, and which key each
 * feature uses. Backend: routes/admin-ai.js.
 *
 * What drives the design:
 *  - A key is typed once and never shown again: the list shows its last four
 *    characters and a fingerprint, editing leaves the key field empty ("blank
 *    keeps it"), and a new host or provider needs the key again.
 *  - Saving tests the key first: a refused key is never saved; a busy
 *    provider saves it as untested.
 *  - Prices are typed in US dollars per million tokens and kept in micro-USD;
 *    a feature may only use a deployment that has prices, so every request's
 *    cost is recorded.
 *  - Disabling a key stops its use at once; a key a feature uses cannot be
 *    deleted.
 *  - A monthly budget either warns or stops requests; the bar shows what was
 *    spent, what requests in flight may still cost, and the limit, in words
 *    as well as colour.
 */

const QUERY = ['ai-admin'];
const PROVIDER_LABELS = { 'azure-openai': 'Azure OpenAI', openai: 'OpenAI' };
const ENDPOINT_HINTS = {
  'azure-openai': 'The resource\'s v1 endpoint: https://<resource>.openai.azure.com/openai/v1 or https://<resource>.services.ai.azure.com/openai/v1. The chat completions URL works too.',
  openai: 'https://api.openai.com/v1, or an OpenAI-compatible service\'s /v1 address.',
};

/** "1.25" (USD per million tokens) -> 1250000 micro-USD; '' -> null; anything else -> NaN. */
export function usdToMicro(value) {
  const text = String(value ?? '').trim();
  if (text === '') return null;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 1e6) : NaN;
}
export const microToUsd = (micro) => (micro === null || micro === undefined ? '' : String(micro / 1e6));

/** Micro-USD as dollars: cents for sums, four decimals below a dollar (single requests cost fractions of a cent). */
export function formatUsd(micro) {
  const usd = (micro || 0) / 1e6;
  return usd.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2,
    maximumFractionDigits: usd !== 0 && Math.abs(usd) < 1 ? 4 : 2 });
}
const compact = (n) => Number(n || 0).toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const errorOf = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || fallback;
const dateOf = (value) => (value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');

const iconBtnStyle = (color) => ({
  background: 'none', border: '1px solid var(--border-primary)', borderRadius: 6, color: color || 'var(--text-secondary)',
  cursor: 'pointer', padding: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});
const monoStyle = { fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-all' };
const noteStyle = (tone) => ({
  padding: '10px 14px', borderRadius: 8, fontSize: 13.5, lineHeight: 1.55, display: 'flex', alignItems: 'flex-start', gap: 8,
  background: tone === 'warning' ? 'var(--badge-warning-bg)' : 'var(--bg-tertiary)',
  border: `1px solid ${tone === 'warning' ? 'var(--accent-warning)' : 'var(--border-primary)'}`,
  color: tone === 'warning' ? 'var(--accent-warning)' : 'var(--text-secondary)',
});

function pricesLine(prices) {
  if (prices?.inputPerM === null || prices?.inputPerM === undefined) return 'no prices';
  const parts = [`${formatUsd(prices.inputPerM)} in`];
  if (prices.cachedInputPerM !== null && prices.cachedInputPerM !== undefined) parts.push(`${formatUsd(prices.cachedInputPerM)} cached`);
  if (prices.cacheWritePerM !== null && prices.cacheWritePerM !== undefined) parts.push(`${formatUsd(prices.cacheWritePerM)} cache write`);
  parts.push(`${formatUsd(prices.outputPerM)} out`);
  return `${parts.join(' · ')} per 1M tokens`;
}

/** A key's month against its budget: spent, what requests in flight may still cost, the limit. */
function BudgetBar({ state }) {
  if (!state || state.limitMicroUsd === null || state.limitMicroUsd === undefined) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>No budget</div>;
  }
  const limit = state.limitMicroUsd;
  const spentShare = limit > 0 ? Math.min(1, state.spentMicroUsd / limit) : 1;
  const flightShare = limit > 0 ? Math.min(1 - spentShare, state.reservedMicroUsd / limit) : 0;
  const color = state.state === 'reached' ? 'var(--accent-danger)' : state.state === 'warning' ? 'var(--accent-warning)' : 'var(--accent-success)';
  const verdict = state.state === 'reached' ? (state.action === 'block' ? 'used up: requests stopped' : 'over budget')
    : state.state === 'warning' ? `past ${state.warnPercent}%` : null;
  return (
    <div style={{ marginTop: 6, minWidth: 170 }} data-testid="ai-budget">
      <div role="meter" aria-label="Budget used this month" aria-valuemin={0} aria-valuemax={limit / 1e6} aria-valuenow={state.spentMicroUsd / 1e6}
        aria-valuetext={`${formatUsd(state.spentMicroUsd)} of ${formatUsd(limit)}`}
        style={{ height: 6, borderRadius: 3, background: 'var(--bg-tertiary)', overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: `${spentShare * 100}%`, background: color }} />
        <div style={{ width: `${flightShare * 100}%`, background: color, opacity: 0.35 }} />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, whiteSpace: 'normal' }}>
        {formatUsd(state.spentMicroUsd)} of {formatUsd(limit)}
        {state.reservedMicroUsd > 0 ? ` · up to ${formatUsd(state.reservedMicroUsd)} in flight` : ''}
        {verdict && <strong style={{ color, fontWeight: 600 }}> · {verdict}</strong>}
        {state.action === 'block' && !verdict ? ' · stops at the limit' : ''}
      </div>
    </div>
  );
}

/** The last test of a key, in a few words. */
function TestState({ test }) {
  if (!test) return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not tested</span>;
  const code = test.httpStatus ? `HTTP ${test.httpStatus}${test.providerCode ? `: ${test.providerCode}` : ''}` : (test.providerCode || 'unreachable');
  const [Icon, color, text] = test.ok ? [CheckCircle2, 'var(--accent-success)', `Working · ${test.latencyMs} ms`]
    : test.untested ? [Clock, 'var(--accent-warning)', `Saved untested (${code})`]
      : [XCircle, 'var(--accent-danger)', `Failed (${code})`];
  return (
    <span title={`${dateOf(test.at)}${test.apiModel ? ` · ${test.apiModel}` : ''}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color }}>
      <Icon size={13} aria-hidden="true" /> {text}
    </span>
  );
}

function AiProvidersManager() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: QUERY, queryFn: aiAdmin.get, retry: false });
  const [editing, setEditing] = useState(null); // 'new' or a key
  const [deleting, setDeleting] = useState(null);
  const [disabling, setDisabling] = useState(null);
  const [importing, setImporting] = useState(false);
  const [testing, setTesting] = useState({}); // id -> 'pending' | error text
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY });

  const data = query.data;
  const credentials = data?.credentials || [];
  const settings = data?.settings;
  const masterKey = data?.masterKey;
  const environment = settings?.environment;

  const enable = useMutation({
    mutationFn: (credential) => aiAdmin.setStatus(credential.id, 'active', credential.revision),
    onSuccess: refresh,
  });

  async function test(credential) {
    setTesting((t) => ({ ...t, [credential.id]: 'pending' }));
    try {
      await aiAdmin.test(credential.id);
      setTesting((t) => ({ ...t, [credential.id]: null }));
    } catch (error) {
      setTesting((t) => ({ ...t, [credential.id]: errorOf(error, 'The test could not be run.') }));
    }
    refresh();
  }

  return (
    <div className="card" style={{ marginBottom: 24 }} data-testid="ai-providers">
      <div className="card-header">
        <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeyRound size={20} aria-hidden="true" />
          AI providers
        </h2>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')} disabled={!masterKey?.present}
          data-testid="ai-add-key" style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: masterKey?.present ? 1 : 0.5 }}
          title={masterKey?.present ? undefined : 'Keys cannot be saved without the master key'}>
          <Plus size={16} aria-hidden="true" /> Add key
        </button>
      </div>

      <p className="text-muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 16, maxWidth: '80ch' }}>
        The keys the AI features use (today, Lab insights). A key is stored encrypted, is sent only to the host it was
        saved for, and is never shown again. Every request made with it is recorded with its tokens, its cost at the
        prices below, and the staff member who started it.
      </p>

      {query.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }} role="status" aria-label="Loading AI providers">
          <Loader size={28} className="spinner" aria-hidden="true" />
        </div>
      ) : query.isError ? (
        <div role="alert" style={noteStyle('warning')}>
          <AlertTriangle size={15} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{errorOf(query.error, 'The AI settings could not be read.')}</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {!masterKey?.present && (
            <div role="alert" style={noteStyle('warning')} data-testid="ai-no-master-key">
              <AlertTriangle size={15} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>Keys cannot be saved or used: {masterKey?.reason || 'the master key is missing.'}</span>
            </div>
          )}

          {environment?.inUse && (
            <div style={noteStyle()} data-testid="ai-environment-in-use">
              <Info size={15} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>
                Lab insights uses the key in the platform's environment ({environment.model} at {environment.host}), whose
                requests are recorded without a cost. Import it to manage it here, with its prices.
              </span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setImporting(true)} disabled={!masterKey?.present}
                style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }} data-testid="ai-import-environment">
                <ArrowDownToLine size={14} aria-hidden="true" /> Import it
              </button>
            </div>
          )}

          {settings && <Assignments settings={settings} credentials={credentials} onSaved={refresh} />}

          {credentials.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
              <KeyRound size={36} style={{ marginBottom: 10, opacity: 0.3 }} aria-hidden="true" />
              <p style={{ fontSize: 15, marginBottom: 4 }}>No keys saved</p>
              <p style={{ fontSize: 13, margin: 0 }}>Add a key, or import the environment's, to manage it here.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" data-testid="ai-keys-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Provider</th>
                    <th>Deployments and prices</th>
                    <th>This month</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.map((c) => (
                    <tr key={c.id} data-testid={`ai-key-row-${c.id}`}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          <code style={{ ...monoStyle, fontSize: 11 }}>{c.keyHint}</code>
                          {c.fingerprint && <span title="A digest of the key, to tell two keys apart"> · {c.fingerprint}</span>}
                        </div>
                        {c.assignedTo?.length > 0 && (
                          <div style={{ fontSize: 12, color: 'var(--accent-primary)', marginTop: 4 }}>Used for {c.assignedTo.join(', ')}</div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>{PROVIDER_LABELS[c.provider] || c.provider}</div>
                        <code style={{ ...monoStyle, fontSize: 11 }}>{c.host}</code>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {c.deployments.map((d) => (
                          <div key={d.name} style={{ marginBottom: 2 }}>
                            <code style={{ ...monoStyle, color: 'var(--text-primary)' }}>{d.name}</code>
                            <span style={{ color: d.priced ? 'var(--text-secondary)' : 'var(--accent-warning)', fontSize: 12 }}> · {pricesLine(d.prices)}</span>
                          </div>
                        ))}
                      </td>
                      <td style={{ fontSize: 13, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        <div style={{ fontWeight: 600 }}>{formatUsd(c.month?.costMicroUsd)}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {compact(c.month?.calls)} call{c.month?.calls === 1 ? '' : 's'} · {compact(c.month?.inputTokens)} in · {compact(c.month?.outputTokens)} out
                        </div>
                        <BudgetBar state={c.budgetState} />
                      </td>
                      <td>
                        <span className={c.status === 'active' ? 'badge badge-success' : 'badge badge-warning'}>
                          {c.status === 'active' ? 'Enabled' : 'Disabled'}
                        </span>
                        <div style={{ marginTop: 4 }}>
                          {testing[c.id] === 'pending'
                            ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Testing…</span>
                            : testing[c.id]
                              ? <span role="alert" style={{ fontSize: 12, color: 'var(--accent-danger)' }}>{testing[c.id]}</span>
                              : <TestState test={c.lastTest} />}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button title="Send a short test request" aria-label={`Test ${c.name}`} data-testid={`ai-test-${c.id}`}
                            onClick={() => test(c)} disabled={testing[c.id] === 'pending'} style={iconBtnStyle('var(--accent-primary)')}>
                            <PlugZap size={15} aria-hidden="true" />
                          </button>
                          <button title="Edit" aria-label={`Edit ${c.name}`} data-testid={`ai-edit-${c.id}`}
                            onClick={() => setEditing(c)} style={iconBtnStyle()}>
                            <Pencil size={15} aria-hidden="true" />
                          </button>
                          {c.status === 'active' ? (
                            <button title="Disable: stops its use at once" aria-label={`Disable ${c.name}`} data-testid={`ai-disable-${c.id}`}
                              onClick={() => setDisabling(c)} style={iconBtnStyle('var(--accent-warning)')}>
                              <PowerOff size={15} aria-hidden="true" />
                            </button>
                          ) : (
                            <button title="Enable" aria-label={`Enable ${c.name}`} data-testid={`ai-enable-${c.id}`}
                              onClick={() => enable.mutate(c)} disabled={enable.isPending} style={iconBtnStyle('var(--accent-success)')}>
                              <Power size={15} aria-hidden="true" />
                            </button>
                          )}
                          <button title={c.assignedTo?.length ? 'In use: assign another key first' : 'Delete'} aria-label={`Delete ${c.name}`}
                            data-testid={`ai-delete-${c.id}`} onClick={() => setDeleting(c)} disabled={c.assignedTo?.length > 0}
                            style={{ ...iconBtnStyle('var(--accent-danger)'), opacity: c.assignedTo?.length ? 0.4 : 1, cursor: c.assignedTo?.length ? 'not-allowed' : 'pointer' }}>
                            <Trash2 size={15} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {enable.isError && <div role="alert" className="dlg-alert" style={{ margin: 0 }}>{errorOf(enable.error, 'The key could not be enabled.')}</div>}

          <p className="text-muted" style={{ fontSize: 12.5, margin: 0 }}>
            Every request, its cost and who started it: <Link to="/ai-usage" data-testid="ai-usage-link">AI usage</Link>.
            {' '}Keys may be sent only to {data.allowedHosts.length ? data.allowedHosts.join(', ') : 'no host yet'} (set by the
            platform's operators, AI_ALLOWED_HOSTS).
            {masterKey?.present && <> Master key {masterKey.kid}{masterKey.previousKid ? `, rotating from ${masterKey.previousKid}` : ''}.</>}
          </p>
        </div>
      )}

      {editing && (
        <KeyDialog credential={editing === 'new' ? null : editing} allowedHosts={data?.allowedHosts || []}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
      {disabling && <DisableDialog credential={disabling} onClose={() => setDisabling(null)} onSaved={() => { setDisabling(null); refresh(); }} />}
      {deleting && <DeleteDialog credential={deleting} onClose={() => setDeleting(null)} onSaved={() => { setDeleting(null); refresh(); }} />}
      {importing && environment && (
        <ImportDialog environment={environment} onClose={() => setImporting(false)} onSaved={() => { setImporting(false); refresh(); }} />
      )}
    </div>
  );
}

/* ------------------------------ Assignments ------------------------------ */

const choiceOf = (assignment) => (assignment ? `${assignment.credential}|${assignment.deployment}` : '');
const assignmentOf = (choice) => {
  if (!choice) return null;
  const at = choice.indexOf('|');
  return { credential: choice.slice(0, at), deployment: choice.slice(at + 1) };
};

function Assignments({ settings, credentials, onSaved }) {
  const saved = { report: choiceOf(settings.labInsights.report), story: choiceOf(settings.labInsights.story) };
  const [report, setReport] = useState(saved.report);
  const [story, setStory] = useState(saved.story);
  // A save (or another admin's) brings a new revision: show what is saved now.
  useEffect(() => { setReport(saved.report); setStory(saved.story); }, [settings.revision]);

  const options = useMemo(() => credentials.flatMap((c) => c.deployments.map((d) => ({
    value: `${c.id}|${d.name}`, label: `${c.name} · ${d.name}${c.status !== 'active' ? ' (disabled)' : !d.priced ? ' (no prices)' : ''}`,
    usable: c.status === 'active' && d.priced, credential: c,
  }))), [credentials]);
  const chosen = (value) => options.find((o) => o.value === value);
  const mutation = useMutation({
    mutationFn: () => aiAdmin.assign({ revision: settings.revision, labInsights: { report: assignmentOf(report), story: report ? assignmentOf(story) : null } }),
    onSuccess: onSaved,
  });
  const changed = report !== saved.report || story !== saved.story;
  const disabledAssigned = [chosen(saved.report), chosen(saved.story)].filter((o) => o && o.credential.status !== 'active');

  return (
    <div style={{ border: '1px solid var(--border-primary)', borderRadius: 8, padding: '14px 16px', display: 'grid', gap: 12 }} data-testid="ai-assignments">
      <div style={{ fontWeight: 600, fontSize: 14 }}>Lab insights uses</div>
      <div className="form-grid form-grid-2">
        <Field label="Student and class reports" hint="Reads each student's measurements, code and key screenshots.">
          <select value={report} onChange={(e) => setReport(e.target.value)} data-testid="ai-assign-report">
            <option value="">{settings.environment.inUse ? 'The environment\'s key (not managed here)' : 'No key: analyses are measured only'}</option>
            {options.map((o) => <option key={o.value} value={o.value} disabled={!o.usable && o.value !== saved.report}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Scene captions" hint="Describes small screenshots of the session; a smaller model will do.">
          <select value={report ? story : ''} onChange={(e) => setStory(e.target.value)} disabled={!report} data-testid="ai-assign-story">
            <option value="">The reports' key and deployment</option>
            {options.map((o) => <option key={o.value} value={o.value} disabled={!o.usable && o.value !== saved.story}>{o.label}</option>)}
          </select>
        </Field>
      </div>
      {disabledAssigned.length > 0 && (
        <div role="status" style={noteStyle('warning')}>
          <AlertTriangle size={15} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{disabledAssigned[0].credential.name} is disabled: until it is enabled or another key is chosen, analyses are measured only.</span>
        </div>
      )}
      {settings.environment.inUse && report && !saved.report && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          Once a key is chosen here, the environment's key is no longer used, even if the choice is cleared later.
        </p>
      )}
      {mutation.isError && <div role="alert" className="dlg-alert" style={{ margin: 0 }}>{errorOf(mutation.error, 'The choice could not be saved.')}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {changed && (
          <button type="button" className="btn btn-sm" onClick={() => { setReport(saved.report); setStory(saved.story); mutation.reset(); }}
            disabled={mutation.isPending}>Undo</button>
        )}
        <button type="button" className="btn btn-primary btn-sm" onClick={() => mutation.mutate()} disabled={!changed || mutation.isPending}
          data-testid="ai-assign-save" style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: changed ? 1 : 0.5 }}>
          {mutation.isPending && <Loader size={14} className="spinner" aria-hidden="true" />} Save
        </button>
      </div>
    </div>
  );
}

/* ------------------------------ Add / edit a key ------------------------------ */

// Rows keep their own key, so removing one in the middle never moves another's inputs.
let rowCount = 0;
const blankDeployment = () => ({ row: ++rowCount, name: '', reasoning: 'auto', input: '', cached: '', write: '', output: '' });
const formOf = (d) => ({
  row: ++rowCount, name: d.name, reasoning: d.reasoning === true ? 'yes' : d.reasoning === false ? 'no' : 'auto',
  input: microToUsd(d.prices?.inputPerM), cached: microToUsd(d.prices?.cachedInputPerM), write: microToUsd(d.prices?.cacheWritePerM),
  output: microToUsd(d.prices?.outputPerM),
});
const PRICE_FIELDS = [['input', 'Input'], ['cached', 'Cached input'], ['write', 'Cache write'], ['output', 'Output']];
const hostOf = (value) => { try { return new URL(String(value).trim()).hostname.toLowerCase(); } catch { return null; } };

/** The deployments as the API takes them, or a sentence saying what is wrong. */
export function deploymentsPayload(rows) {
  const named = rows.filter((d) => d.name.trim() || d.input || d.output || d.cached || d.write);
  if (!named.length) return { error: 'Add at least one deployment: the Azure deployment name, or the OpenAI model.' };
  const out = [];
  for (const d of named) {
    if (!d.name.trim()) return { error: 'Every deployment needs its name.' };
    const prices = { inputPerM: usdToMicro(d.input), cachedInputPerM: usdToMicro(d.cached), cacheWritePerM: usdToMicro(d.write), outputPerM: usdToMicro(d.output) };
    if (Object.values(prices).some((v) => Number.isNaN(v))) return { error: `The prices of ${d.name.trim()} must be amounts in US dollars per million tokens.` };
    if ((prices.inputPerM === null) !== (prices.outputPerM === null)) return { error: `Give ${d.name.trim()} both an input and an output price, or neither.` };
    out.push({ name: d.name.trim(), reasoning: d.reasoning === 'auto' ? null : d.reasoning === 'yes', prices });
  }
  return { deployments: out };
}

function KeyDialog({ credential, allowedHosts, onClose, onSaved }) {
  const isEdit = !!credential;
  const [name, setName] = useState(credential?.name || '');
  const [provider, setProvider] = useState(credential?.provider || 'azure-openai');
  const [endpoint, setEndpoint] = useState(credential?.endpoint || '');
  const [key, setKey] = useState('');
  const [maxConcurrent, setMaxConcurrent] = useState(String(credential?.maxConcurrent ?? 4));
  const [budget, setBudget] = useState(microToUsd(credential?.budget?.monthlyMicroUsd));
  const [warnPercent, setWarnPercent] = useState(String(credential?.budget?.warnPercent ?? 80));
  const [budgetAction, setBudgetAction] = useState(credential?.budget?.action || 'warn');
  const [rows, setRows] = useState(credential ? credential.deployments.map(formOf) : [blankDeployment()]);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);

  const moved = isEdit && (provider !== credential.provider || (hostOf(endpoint) && hostOf(endpoint) !== credential.host));
  const keyNeeded = !isEdit || moved;
  const host = hostOf(endpoint);
  const hostRefused = host && allowedHosts.length > 0 && !allowedHosts.includes(host);

  const save = useMutation({
    mutationFn: (payload) => (isEdit ? aiAdmin.update(credential.id, payload) : aiAdmin.create(payload)),
    onSuccess: () => onSaved(),
    onError: (err) => setError(errorOf(err, 'The key could not be saved.')),
  });
  const test = useMutation({
    mutationFn: () => (key ? aiAdmin.testCandidate({ provider, endpoint, key, deployment: rows[0]?.name.trim(), reasoning: deploymentsPayload(rows).deployments?.[0]?.reasoning ?? null })
      : aiAdmin.test(credential.id, rows[0]?.name.trim())),
    onSuccess: (res) => { setError(''); setTestResult({ at: new Date().toISOString(), ...res.result }); },
    onError: (err) => { setTestResult(null); setError(errorOf(err, 'The test could not be run.')); },
  });
  const busy = save.isPending || test.isPending;

  function changeProvider(value) {
    setProvider(value);
    if (value === 'openai' && !endpoint.trim()) setEndpoint('https://api.openai.com/v1');
  }
  const setRow = (i, field, value) => setRows((list) => list.map((row, at) => (at === i ? { ...row, [field]: value } : row)));

  function submit(e) {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Give the key a name.'); return; }
    if (!endpoint.trim()) { setError('Enter the endpoint.'); return; }
    if (keyNeeded && !key) { setError(isEdit ? 'A new host or provider needs the key again.' : 'Enter the key.'); return; }
    const { deployments, error: invalid } = deploymentsPayload(rows);
    if (invalid) { setError(invalid); return; }
    const concurrent = parseInt(maxConcurrent, 10);
    if (!(concurrent >= 1 && concurrent <= 32)) { setError('Requests at once must be a whole number from 1 to 32.'); return; }
    const monthly = usdToMicro(budget);
    if (Number.isNaN(monthly)) { setError('The monthly budget must be an amount in US dollars, or empty for none.'); return; }
    const warn = parseInt(warnPercent, 10);
    if (monthly !== null && !(warn >= 1 && warn <= 100)) { setError('Warn at must be a whole percentage from 1 to 100.'); return; }
    const payload = { name: name.trim(), provider, endpoint: endpoint.trim(), deployments, maxConcurrent: concurrent,
      budget: monthly === null ? { monthlyMicroUsd: null, warnPercent: 80, action: 'warn' } : { monthlyMicroUsd: monthly, warnPercent: warn, action: budgetAction } };
    if (key) payload.key = key;
    if (isEdit) payload.revision = credential.revision;
    save.mutate(payload);
  }

  return (
    <Dialog size="lg" onClose={busy ? undefined : onClose} labelledBy="ai-key-title">
      <DialogHeader id="ai-key-title" title={isEdit ? `Edit ${credential.name}` : 'Add an AI key'}
        icon={<KeyRound size={20} aria-hidden="true" />} onClose={busy ? undefined : onClose} />
      <form className="dlg-form" onSubmit={submit} aria-label={isEdit ? 'Edit AI key' : 'Add AI key'}>
        <DialogBody>
          {error && <div role="alert" className="dlg-alert">{error}</div>}

          <FormSection title="Provider">
            <div className="form-grid form-grid-21">
              <Field label="Name" required hint="How this key is listed here and in the usage records.">
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Lab insights (Azure, East US 2)" data-testid="ai-key-name" />
              </Field>
              <Field label="Provider" required>
                <select value={provider} onChange={(e) => changeProvider(e.target.value)} data-testid="ai-key-provider">
                  <option value="azure-openai">Azure OpenAI</option>
                  <option value="openai">OpenAI</option>
                </select>
              </Field>
            </div>
            <Field label="Endpoint" required hint={ENDPOINT_HINTS[provider]}>
              <input type="url" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} spellCheck={false} data-testid="ai-key-endpoint"
                placeholder={provider === 'openai' ? 'https://api.openai.com/v1' : 'https://<resource>.openai.azure.com/openai/v1'}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
            </Field>
            {hostRefused && (
              <p role="status" style={{ ...noteStyle('warning'), marginTop: -6, marginBottom: 14 }}>
                <AlertTriangle size={14} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{host} is not an allowed AI host. The platform's operators decide which hosts may receive its data: {allowedHosts.join(', ')}.</span>
              </p>
            )}
            <Field label="Key" required={keyNeeded}
              hint={isEdit
                ? (moved ? 'A new host or provider needs the key again: the saved key is only ever sent where it was saved for.'
                  : `Leave empty to keep the saved key (${credential.keyHint}). It cannot be shown.`)
                : 'Stored encrypted and never shown again. Saving sends one short test request first.'}>
              <input type="password" value={key} onChange={(e) => setKey(e.target.value.trim())} autoComplete="new-password" spellCheck={false}
                data-testid="ai-key-secret" placeholder={isEdit && !moved ? '••••••••••••' : ''} style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
            </Field>
          </FormSection>

          <FormSection title="Deployments and prices">
            <p className="field-hint" style={{ marginTop: -4, marginBottom: 10 }}>
              Azure: the deployment's name; OpenAI: the model. Prices in US dollars per million tokens, from the provider's price
              list: they apply from the moment they are saved, and earlier requests keep the prices they were recorded with.
              Cache write: GPT-5.6 and later bill writes to the prompt cache (1.25 times input); leave it empty for earlier models.
              Reasoning: Auto recognises the GPT-5 family and the o-series.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ minWidth: 760 }}>
                <thead>
                  <tr style={{ fontSize: 12 }}>
                    <th style={{ padding: '6px 6px 6px 0' }}>Deployment or model</th>
                    <th style={{ padding: 6 }}>Reasoning</th>
                    <th style={{ padding: 6 }}>Input $/1M</th>
                    <th style={{ padding: 6 }}>Cached input $/1M</th>
                    <th style={{ padding: 6 }}>Cache write $/1M</th>
                    <th style={{ padding: 6 }}>Output $/1M</th>
                    <th style={{ padding: 6 }} aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.row}>
                      <td style={{ padding: '6px 6px 6px 0' }}>
                        <input type="text" value={row.name} onChange={(e) => setRow(i, 'name', e.target.value)} spellCheck={false}
                          aria-label={`Deployment ${i + 1} name`} placeholder="gpt-5.6-sol" data-testid={`ai-deployment-name-${i}`}
                          style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
                      </td>
                      <td style={{ padding: 6 }}>
                        <select value={row.reasoning} onChange={(e) => setRow(i, 'reasoning', e.target.value)} aria-label={`Deployment ${i + 1} reasoning`}>
                          <option value="auto">Auto</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </td>
                      {PRICE_FIELDS.map(([field, label]) => (
                        <td key={field} style={{ padding: 6 }}>
                          <input type="text" inputMode="decimal" value={row[field]} onChange={(e) => setRow(i, field, e.target.value)}
                            aria-label={`Deployment ${i + 1} ${label.toLowerCase()} price`} placeholder={['cached', 'write'].includes(field) ? 'optional' : '0.00'}
                            data-testid={`ai-deployment-${field}-${i}`} style={{ fontVariantNumeric: 'tabular-nums' }} />
                        </td>
                      ))}
                      <td style={{ padding: 6 }}>
                        <button type="button" aria-label={`Remove deployment ${i + 1}`} onClick={() => setRows((list) => list.filter((_, at) => at !== i))}
                          disabled={rows.length === 1} style={{ ...iconBtnStyle(), opacity: rows.length === 1 ? 0.4 : 1 }}>
                          <X size={14} aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-sm" onClick={() => setRows((list) => [...list, blankDeployment()])} disabled={rows.length >= 20}
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <Plus size={14} aria-hidden="true" /> Add a deployment
            </button>
          </FormSection>

          <FormSection title="Limits">
            <Field label="Requests at once" hint="At most this many requests in flight with this key, per backend replica; the others wait their turn.">
              <input type="number" min={1} max={32} value={maxConcurrent} onChange={(e) => setMaxConcurrent(e.target.value)} style={{ maxWidth: 120 }} data-testid="ai-key-concurrency" />
            </Field>
          </FormSection>

          <FormSection title="Monthly budget">
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <Field label="Budget, US dollars a month" hint="Empty: no budget (the spend is still recorded).">
                <input type="text" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="none"
                  data-testid="ai-key-budget" style={{ fontVariantNumeric: 'tabular-nums' }} />
              </Field>
              <Field label="Warn at, % of it">
                <input type="number" min={1} max={100} value={warnPercent} onChange={(e) => setWarnPercent(e.target.value)} disabled={!budget.trim()}
                  data-testid="ai-key-warn" />
              </Field>
              <Field label="When it is used up">
                <select value={budgetAction} onChange={(e) => setBudgetAction(e.target.value)} disabled={!budget.trim()} data-testid="ai-key-budget-action">
                  <option value="warn">Only show it here</option>
                  <option value="block">Stop sending requests</option>
                </select>
              </Field>
            </div>
            <p className="field-hint" style={{ marginTop: 4 }}>
              Calendar months, Qatar time. "Stop" never sends a request that could take the month past the budget, counting the
              requests in flight; analyses then go on without AI and say why. Only what Virtual Labs sends with this key is counted:
              its use anywhere else is not, so a budget alert with the provider is still worth setting.
            </p>
          </FormSection>

          {testResult && (
            <div role="status" style={noteStyle(testResult.ok ? undefined : 'warning')} data-testid="ai-key-test-result">
              <TestState test={testResult} />
              {testResult.ok && testResult.apiModel && <span style={{ fontSize: 12 }}>answered by {testResult.apiModel}</span>}
            </div>
          )}
        </DialogBody>
        <DialogFooter spread>
          <button type="button" className="btn btn-secondary" onClick={() => test.mutate()} data-testid="ai-key-test"
            disabled={busy || (keyNeeded && !key) || !rows[0]?.name.trim() || (!!key && !endpoint.trim())}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {test.isPending ? <Loader size={15} className="spinner" aria-hidden="true" /> : <PlugZap size={15} aria-hidden="true" />}
            {key || !isEdit ? 'Test' : 'Test the saved key'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="ai-key-save" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {save.isPending && <Loader size={15} className="spinner" aria-hidden="true" />} {save.isPending ? 'Testing and saving…' : 'Save'}
            </button>
          </div>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/* ------------------------------ Disable, delete, import ------------------------------ */

function DisableDialog({ credential, onClose, onSaved }) {
  const mutation = useMutation({ mutationFn: () => aiAdmin.setStatus(credential.id, 'disabled', credential.revision), onSuccess: onSaved });
  return (
    <Dialog size="sm" onClose={mutation.isPending ? undefined : onClose} labelledBy="ai-disable-title" confirmOnClose={false}>
      <DialogHeader id="ai-disable-title" title={`Disable ${credential.name}`} icon={<PowerOff size={18} aria-hidden="true" />} onClose={mutation.isPending ? undefined : onClose} />
      <DialogBody>
        {mutation.isError && <div role="alert" className="dlg-alert">{errorOf(mutation.error, 'The key could not be disabled.')}</div>}
        <p style={{ marginTop: 0 }}>The key is not used again from now on, by any request, until it is enabled.</p>
        {credential.assignedTo?.length > 0 && (
          <p style={noteStyle('warning')}>
            <AlertTriangle size={15} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
            <span>It is used for {credential.assignedTo.join(' and ')}: analyses are measured only until it is enabled or another key is chosen, including the students of an analysis running now.</span>
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={() => mutation.mutate()} disabled={mutation.isPending} data-testid="ai-confirm-disable"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {mutation.isPending ? <Loader size={15} className="spinner" aria-hidden="true" /> : <PowerOff size={15} aria-hidden="true" />} Disable
        </button>
      </DialogFooter>
    </Dialog>
  );
}

function DeleteDialog({ credential, onClose, onSaved }) {
  const mutation = useMutation({ mutationFn: () => aiAdmin.remove(credential.id, credential.revision), onSuccess: onSaved });
  return (
    <Dialog size="sm" onClose={mutation.isPending ? undefined : onClose} labelledBy="ai-delete-title" confirmOnClose={false}>
      <DialogHeader id="ai-delete-title" title={`Delete ${credential.name}`} icon={<Trash2 size={18} aria-hidden="true" />} onClose={mutation.isPending ? undefined : onClose} />
      <DialogBody>
        {mutation.isError && <div role="alert" className="dlg-alert">{errorOf(mutation.error, 'The key could not be deleted.')}</div>}
        <p style={{ marginTop: 0 }}>
          The saved key ({credential.keyHint}) is erased and cannot be recovered. Its name stays in the usage records and the audit
          log, which keep referring to it.
        </p>
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={() => mutation.mutate()} disabled={mutation.isPending} data-testid="ai-confirm-delete"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {mutation.isPending ? <Loader size={15} className="spinner" aria-hidden="true" /> : <Trash2 size={15} aria-hidden="true" />} Delete
        </button>
      </DialogFooter>
    </Dialog>
  );
}

function ImportDialog({ environment, onClose, onSaved }) {
  const names = [...new Set([environment.model, environment.storyModel].filter(Boolean))];
  const [name, setName] = useState('Lab insights (imported)');
  const [prices, setPrices] = useState(Object.fromEntries(names.map((n) => [n, { input: '', cached: '', write: '', output: '' }])));
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: (payload) => aiAdmin.importEnvironment(payload),
    onSuccess: onSaved,
    onError: (err) => setError(errorOf(err, 'The environment\'s key could not be imported.')),
  });
  function submit(e) {
    e.preventDefault();
    setError('');
    const payload = {};
    for (const n of names) {
      const { deployments, error: invalid } = deploymentsPayload([{ name: n, reasoning: 'auto', ...prices[n] }]);
      if (invalid) { setError(invalid); return; }
      if (deployments[0].prices.inputPerM === null) { setError(`Give ${n} its prices: Lab insights will use it, and its cost is recorded from them.`); return; }
      payload[n] = deployments[0].prices;
    }
    mutation.mutate({ name: name.trim() || undefined, prices: payload });
  }
  const set = (n, field, value) => setPrices((p) => ({ ...p, [n]: { ...p[n], [field]: value } }));
  return (
    <Dialog size="md" onClose={mutation.isPending ? undefined : onClose} labelledBy="ai-import-title">
      <DialogHeader id="ai-import-title" title="Import the environment's key" icon={<ArrowDownToLine size={18} aria-hidden="true" />} onClose={mutation.isPending ? undefined : onClose} />
      <form className="dlg-form" onSubmit={submit}>
        <DialogBody>
          {error && <div role="alert" className="dlg-alert">{error}</div>}
          <p style={{ marginTop: 0, fontSize: 14, lineHeight: 1.6 }}>
            The server copies the key Lab insights uses today ({environment.host}) into Settings, encrypted, tests it, and
            assigns it to Lab insights. The key never reaches this page. From then on the environment's key is not used.
          </p>
          <Field label="Name"><input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} /></Field>
          {names.map((n) => (
            <FormSection key={n} title={`${n}${n === environment.model ? ' · reports' : ''}${n === environment.storyModel ? ' · scene captions' : ''}`}>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
                {PRICE_FIELDS.map(([field, label]) => (
                  <Field key={field} label={`${label} $/1M`} required={!['cached', 'write'].includes(field)}>
                    <input type="text" inputMode="decimal" value={prices[n][field]} onChange={(e) => set(n, field, e.target.value)}
                      placeholder={['cached', 'write'].includes(field) ? 'optional' : '0.00'} data-testid={`ai-import-${field}-${n}`} />
                  </Field>
                ))}
              </div>
            </FormSection>
          ))}
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={mutation.isPending} data-testid="ai-import-confirm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {mutation.isPending && <Loader size={15} className="spinner" aria-hidden="true" />} Import and use it
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export default AiProvidersManager;
